import { Helmet } from 'react-helmet-async';
import { Container, Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { PaginatedState } from 'src/models/paginatedState';
import { CheckoutPagesService } from 'src/api/services/CheckoutPagesService';
import CheckoutPagesTable from './CheckoutPagesTable';
import { CheckoutPage } from 'src/models/checkoutPage';
import CheckoutPageDialog from './CheckoutPageDialog';
import CheckoutPageActivationDialog from './CheckoutPageActivationDialog';
import { Template } from 'src/models/template';
import { TemplatesService } from 'src/api/services/TemplatesService';
import { PaginatedResponse } from 'src/models/paginatedResponse';
import PageHeader from './PageHeader';
import { Domain } from 'src/models/domain';
import { DomainsService } from 'src/api/services/DomainsService';
import { Company } from 'src/models/company';
import { CompaniesService } from 'src/api/services/CompaniesService';

export interface PageSearchTerms extends Partial<CheckoutPage> {

}

function ApplicationsCheckoutPages() {
  const [paginatedResponse, setPaginatedResponse] = useState<PaginatedResponse<CheckoutPage>>()
  const [paginatedState, setPaginatedState] = useState<PaginatedState>({per_page: 10, page: 0})
  const [searchTerms, setSearchTerms] = useState<PageSearchTerms>({
    name: '',
  })

  const [isSearching, setIsSearching] = useState(false)
  const [companies, setCompanies] = useState<Company[]>([])
  const [domains, setDomains] = useState<Domain[]>([])
  const [templates, setTemplates] = useState<Template[]>([])

  const onSearch = async (terms:PageSearchTerms) => {
    console.log(terms)
    const newTerms = {...terms}
    setSearchTerms(newTerms)
  }

  const onClear = async (terms:PageSearchTerms) => {
    setSearchTerms(terms)
  }

  const refreshData = async () => {
    setIsSearching(true)
    try{
      const response = await CheckoutPagesService.search({
        ...searchTerms,
      }, paginatedState)
      setPaginatedResponse(response)
      if(companies.length <= 0){
        const c = await CompaniesService.getAll();
        setCompanies(c)
      }
      if(domains.length <= 0){
        const d = await DomainsService.getAll();
        setDomains(d)
      }
      if(templates.length <= 0){
        const t = await TemplatesService.getAll('checkout-page');
        setTemplates(t)
      }
    } catch(e){}
    setIsSearching(false)
  }

  useEffect(() => {
    refreshData()
  },[searchTerms, paginatedState])

  const onPageChange = (newPage: number) => {
    let newPaginatedState = {...paginatedState}
    newPaginatedState.page = newPage
    setPaginatedState(newPaginatedState)
  };

  const onRowsPerPageChange = (newRowsPerPage:number) => {
    let newPaginatedState = {...paginatedState}
    newPaginatedState.per_page = newRowsPerPage
    setPaginatedState(newPaginatedState)
  };

  const [isPageDialogOpen, setIsPageDialogOpen] = useState(false)
  const [existingPage, setExistingPage] = useState<CheckoutPage>()

  const onNewPage = () => {
    setExistingPage(undefined)
    setIsPageDialogOpen(true)
  }

  const onEditPage = (page:CheckoutPage) => {
    setExistingPage(page)
    setIsPageDialogOpen(true)
  }

  const onPageDialogClose = (shouldRefresh:boolean) => {
    setIsPageDialogOpen(false)
    if(shouldRefresh){
      refreshData()
    }
  }

  const [isPageActivationDialogOpen, setIsPageActivationDialogOpen] = useState(false)

  const onActivatePage = (page:CheckoutPage) => {
    setExistingPage(page)
    setIsPageActivationDialogOpen(true)
  }

  const onDeactivatePage = (page:CheckoutPage) => {
    setExistingPage(page)
    setIsPageActivationDialogOpen(true)
  }

  const onPageActivationDialogClose = (shouldRefresh:boolean) => {
    setIsPageActivationDialogOpen(false)
    if(shouldRefresh){
      refreshData()
    }
  }

  return (
    <>
      <Helmet>
        <title>Pages</title>
      </Helmet>
      <Container maxWidth="lg">
        <Grid
          container
          spacing={2}
          marginTop={0}
        >
          <Grid item xs={12}>
            {<PageHeader 
              searchTerms={searchTerms} 
              isSearching={isSearching} 
              onSearch={onSearch} 
              onClear={onClear} 
              onNewPage={onNewPage} 
            />}
          </Grid>
          <Grid item xs={12}>
            <CheckoutPagesTable 
              paginatedResponse={paginatedResponse} 
              paginatedState={paginatedState} 
              isRefreshing={isSearching} 
              onPageChange={onPageChange} 
              onRowsPerPageChange={onRowsPerPageChange}
              onEditPage={onEditPage}
              onActivatePage={onActivatePage}
              onDeactivatePage={onDeactivatePage}
            />
          </Grid>
        </Grid>
      </Container>
      <CheckoutPageActivationDialog 
        isOpen={isPageActivationDialogOpen} 
        onClose={onPageActivationDialogClose} 
        existingPage={existingPage} 
      />
      <CheckoutPageDialog 
        isOpen={isPageDialogOpen} 
        onClose={onPageDialogClose} 
        existingPage={existingPage} 
        companies={companies} 
        domains={domains} 
        templates={templates} 
      />
    </>
  );
}

export default ApplicationsCheckoutPages;
